import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, Divider,
    IconButton, CircularProgress, MenuItem, InputAdornment,
    Chip, ListItemText
} from '@mui/material';
import { toastSuccess, alertValidacao } from '../services/alerts';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import api from '../services/api';
import { isVisualizador } from '../services/auth';

const FORMA_LABEL = {
    PIX: 'PIX', DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Crédito', CARTAO_DEBITO: 'Débito',
    BOLETO: 'Boleto', TRANSFERENCIA: 'Transferência',
};

export default function ContasReceberForm() {
    const soLeitura = isVisualizador();
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [planosPagamento, setPlanosPagamento] = useState([]);

    const [formData, setFormData] = useState({
        descricao: '',
        valor: '',
        dataVencimento: '',
        dataPagamento: '',
        numeroRecibo: '',
        observacoes: '',
        statusFinanceiro: 'PENDENTE',
        idAssinaturaPaciente: '',
        idPlanoPagamento: ''
    });

    useEffect(() => {
        api.get('/planos-pagamento?ativo=true')
            .then(r => { const d = r.data; setPlanosPagamento(Array.isArray(d) ? d : (d?.content ?? [])); })
            .catch(() => {});

        if (id) {
            api.get(`/contas-receber/${id}`)
                .then(r => {
                    const d = r.data;
                    setFormData({
                        descricao: d.descricao || '',
                        valor: d.valor || '',
                        dataVencimento: d.dataVencimento ? d.dataVencimento.substring(0, 10) : '',
                        dataPagamento: d.dataPagamento ? d.dataPagamento.substring(0, 10) : '',
                        numeroRecibo: d.numeroRecibo || '',
                        observacoes: d.observacoes || '',
                        statusFinanceiro: d.statusFinanceiro || 'PENDENTE',
                        idAssinaturaPaciente: d.idAssinaturaPaciente || '',
                        idPlanoPagamento: d.idPlanoPagamento || ''
                    });
                })
                .catch(() => {})
                .finally(() => setFetching(false));
        }
    }, [id]);

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

    const handleSave = async () => {
        if (!formData.descricao.trim()) { alertValidacao('A descrição é obrigatória.'); return; }
        if (!formData.valor || parseFloat(formData.valor) <= 0) { alertValidacao('Informe um valor válido.'); return; }
        if (!formData.dataVencimento) { alertValidacao('A data de vencimento é obrigatória.'); return; }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                valor: parseFloat(formData.valor),
                idAssinaturaPaciente: formData.idAssinaturaPaciente ? parseInt(formData.idAssinaturaPaciente) : null,
                idPlanoPagamento: formData.idPlanoPagamento ? parseInt(formData.idPlanoPagamento) : null
            };

            if (id) {
                await api.put(`/contas-receber/${id}`, payload);
            } else {
                await api.post('/contas-receber', payload);
            }

            toastSuccess(id ? 'Conta atualizada com sucesso!' : 'Conta criada com sucesso!');
            setTimeout(() => navigate('/contas-receber'), 800);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/contas-receber')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {soLeitura ? 'Visualizar Conta a Receber' : id ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AttachMoneyIcon fontSize="small" /> Dados do Recebimento
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            sx={{ flex: 2, minWidth: 200 }}
                            label="Descrição *"
                            value={formData.descricao}
                            onChange={set('descricao')}
                            placeholder="Ex: Consulta psicológica — Janeiro/2026"
                            disabled={soLeitura}
                        />
                        <TextField
                            sx={{ flex: 1, minWidth: 140 }}
                            label="Valor (R$) *"
                            type="number"
                            value={formData.valor}
                            onChange={set('valor')}
                            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                            inputProps={{ min: 0, step: '0.01' }}
                            disabled={soLeitura}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            sx={{ flex: 1, minWidth: 160 }}
                            label="Nº do Recibo / NF-e"
                            value={formData.numeroRecibo}
                            onChange={set('numeroRecibo')}
                            placeholder="Ex: REC-0042"
                            disabled={soLeitura}
                        />
                        <TextField select sx={{ flex: 1, minWidth: 160 }} label="Situação *" value={formData.statusFinanceiro} onChange={set('statusFinanceiro')} disabled={soLeitura}>
                            <MenuItem value="PENDENTE">Pendente</MenuItem>
                            <MenuItem value="PAGO">Recebido</MenuItem>
                            <MenuItem value="PARCIAL">Recebido parcialmente</MenuItem>
                            <MenuItem value="VENCIDO">Vencido</MenuItem>
                            <MenuItem value="CANCELADO">Cancelado</MenuItem>
                        </TextField>
                        <TextField
                            select
                            sx={{ flex: 1, minWidth: 220 }}
                            label="Plano de Pagamento"
                            value={formData.idPlanoPagamento}
                            onChange={set('idPlanoPagamento')}
                            disabled={soLeitura}
                            SelectProps={{
                                renderValue: (selected) => {
                                    if (!selected) return <em style={{ color: '#999' }}>Não informado</em>;
                                    const p = planosPagamento.find(x => x.id_p_pagamento === selected);
                                    return p ? p.nome : selected;
                                },
                                MenuProps: { PaperProps: { sx: { maxHeight: 360 } } }
                            }}
                        >
                            <MenuItem value=""><em>Não informado</em></MenuItem>
                            {planosPagamento.map((p) => {
                                const formas = Array.isArray(p.formasPagamento) ? p.formasPagamento : (p.formaPagamento ? [p.formaPagamento] : []);
                                return (
                                    <MenuItem key={p.id_p_pagamento} value={p.id_p_pagamento} sx={{ alignItems: 'flex-start', py: 1.2 }}>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <PaymentIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nome}</Typography>
                                                    {formas.map(f => (
                                                        <Chip key={f} size="small" label={FORMA_LABEL[f] || f} sx={{ height: 20, fontSize: 11 }} />
                                                    ))}
                                                </Box>
                                            }
                                            secondary={
                                                p.descricaoContaBancaria && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                        <AccountBalanceIcon sx={{ fontSize: 14, color: '#888' }} />
                                                        <Typography variant="caption" sx={{ color: '#666' }}>{p.descricaoContaBancaria}</Typography>
                                                    </Box>
                                                )
                                            }
                                        />
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CalendarMonthIcon fontSize="small" /> Datas
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        sx={{ flex: 1, minWidth: 160 }}
                        label="Vencimento *"
                        type="date"
                        value={formData.dataVencimento}
                        onChange={set('dataVencimento')}
                        InputLabelProps={{ shrink: true }}
                        disabled={soLeitura}
                    />
                    <TextField
                        sx={{ flex: 1, minWidth: 160 }}
                        label="Data do recebimento"
                        type="date"
                        value={formData.dataPagamento}
                        onChange={set('dataPagamento')}
                        InputLabelProps={{ shrink: true }}
                        helperText="Preencha somente quando o valor for recebido"
                        disabled={soLeitura}
                    />
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Observações
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Observações"
                    value={formData.observacoes}
                    onChange={set('observacoes')}
                    placeholder="Informações adicionais sobre este recebimento..."
                    disabled={soLeitura}
                />

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/contas-receber')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        {soLeitura ? 'Voltar' : 'Cancelar'}
                    </Button>
                    {!soLeitura && (
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ px: 5 }}
                        >
                            {id ? 'Salvar Alterações' : 'Criar Recebimento'}
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
